import React, { useEffect, useState, useMemo } from 'react';

// custom components
import { Card, CustomSpinner } from '_atoms';
import { TableHeader, TableValue } from '_molecules';
import { ClickableTable } from '_organisms';

// bootstrap
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';

// trremor
import { DateRangePicker, Title as TremorTitle, Subtitle, Flex } from "@tremor/react";

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedFlight, getFlightRouteForecast, selectFlightForecast, selectFlightForecastStatus, selectFlightForecastError } from '_redux/slices/flights/flight-information';
import { selectDepPaxNoShowsFilter, setDepPaxNoShows } from "_redux/slices/prm-bookings/calendar";
import { selectUserDb } from "_redux/slices/main-screen/user";

// styling
import { Colors } from "_styles";

import "./modal-style.css";

// icons
import { MdError } from "react-icons/md";

function getDateOneMonthAgo(currentDate) {
    const currentTimestamp = currentDate.getTime();
    const thirtyDaysAgoTimestamp = currentTimestamp - 2592000000;
    const thirtyDaysAgo = new Date(thirtyDaysAgoTimestamp);
    return thirtyDaysAgo;
}

const FlightForecastTable = (props) => {

    // date value
    const [dateRange, setDateRange] = useState([
        getDateOneMonthAgo(new Date()),
        new Date(),
    ]);

    // redux
    const dispatch = useDispatch();
    const dbUser = useSelector(selectUserDb);

    // dep pax no shows filter
    const depPaxNoShowsFilter = useSelector(selectDepPaxNoShowsFilter);
    const selectedFlight = useSelector(selectSelectedFlight);
    const flightForecast = useSelector(selectFlightForecast);
    const flightForecastStatus = useSelector(selectFlightForecastStatus);
    const flightForecastError = useSelector(selectFlightForecastError);

    //#region use effect
    useEffect(() => {
        // need to get the forecast data for this flight
        if (selectedFlight) {
            refreshFlightForecast();
        }
    }, [selectedFlight]);

    useEffect(() => {
        // need to get the forecast data for this flight
        if (selectedFlight && dateRange[0] != null && dateRange[1] != null) {
            refreshFlightForecast();
        }
    }, [dateRange]);

    useEffect(() => {
        // need to get the forecast data for this flight
        if (selectedFlight && dateRange[0] != null && dateRange[1] != null) {
            refreshFlightForecast();
        }
    }, [depPaxNoShowsFilter]);
    // #endregion

    const refreshFlightForecast = () => {
        // format date
        let startDate = dateRange[0].toISOString().split('T')[0];
        let endDate = dateRange[1].toISOString().split('T')[0];
        let bookingType = ""

        // get type of flight
        if (selectedFlight.type == 'Departures') { bookingType = 'outbound' }
        else { bookingType = 'inbound' }

        if (selectedFlight.flight) {
            dispatch(getFlightRouteForecast(
                {
                    iata: dbUser.details.authority,
                    startDate: startDate,
                    endDate: endDate,
                    flightNumber: selectedFlight.flight,
                    type: bookingType,
                    noShows: depPaxNoShowsFilter
                }
            ))
        }
    }

    //#region review table functions and columns
    const columns = useMemo(
        () => [
            {
                Header: <TableHeader title={"Date"} />,
                accessor: "date",
                Cell: ({ cell: { value } }) => <TableValue title={value} />,
            },
            {
                Header: <TableHeader title={"Time"} />,
                accessor: "hour",
                Cell: ({ cell: { value } }) => <TableValue title={value} />,
            },
            {
                Header: <TableHeader title={"Day"} />,
                accessor: "day",
                Cell: ({ cell: { value } }) => <TableValue title={value} />,
            },
            {
                Header: <TableHeader title={"Actual"} />,
                accessor: "bookingCount",
                Cell: ({ cell: { value } }) => <TableValue title={value} />,
            },
            {
                Header: <TableHeader title={"Forecasted"} />,
                accessor: "forecastedPassengers",
                Cell: ({ cell: { value } }) => <TableValue title={value} />,
            },
        ],
        []
    );
    //#endregion

    return (
        <div
            className='mt-3'
            style={{ marginBottom: 100 }}>

            <Card className='mt-3'>
                <div style={{
                    // borderStyle:'solid', borderWidth:1,
                    minHeight: '600px', maxHeight: '600px',
                    overflowX: 'scroll', overflowY: 'scroll',
                }}>

                    {flightForecastStatus == 'loading' && <CustomSpinner size={'small'} />}
                    <TremorTitle>Flight Forecast</TremorTitle>
                    <Subtitle>View the dates that the flight route is forecasted and the forecasted passenger count</Subtitle>
                    {flightForecastError ? <div style={{ display: 'flex' }}><MdError color={'red'} size={22} /><p style={{ color: 'red' }}>{flightForecastError}</p></div> :

                        <>
                            {/* date range selector / dep pax no shows toggle */}
                            <Row className='mt-2'>
                                <Flex>
                                    <div style={{ display: "flex" }}>
                                        <Form.Check
                                            onClick={(e) => {
                                                dispatch(setDepPaxNoShows(e.target.checked));
                                            }}
                                            type="switch"
                                            defaultChecked={depPaxNoShowsFilter}
                                        />
                                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
                                            Exclude Departure PAX No Shows
                                        </Form.Label>
                                    </div>
                                    <Col xs={4}>
                                        <DateRangePicker
                                            onValueChange={(value) => { setDateRange(value) }}
                                            value={dateRange}
                                            maxWidth="max-w-lg"
                                            enableDropdown={false}
                                            minDate={new Date().setMonth(new Date().getMonth() - 12)} // set the minimum selectable date to 12 months ago 
                                        />
                                    </Col>
                                </Flex>
                            </Row>

                            <Row className='mt-2'>
                                <ClickableTable
                                    columns={columns}
                                    data={flightForecast}
                                    clickable={false}
                                />
                            </Row>
                        </>

                    }
                </div>
            </Card>
        </div>
    );
};

export default FlightForecastTable;
